import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Calculate as CalculateIcon,
  Close as CloseIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { fetchSystemGeneratedMetricsApi } from "../../services/dashboardMatrix";
import { toastMessage } from "../../utils/toastMessage";

const CustomMetricsBuilder = ({ startDate, endDate, onMetricsUpdate }) => {
  const [systemMetrics, setSystemMetrics] = useState({});
  const [customMetrics, setCustomMetrics] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    formula: "",
    description: "",
  });
  const [formulaError, setFormulaError] = useState("");
  const [previewValue, setPreviewValue] = useState(null);

  // Available system metrics for selection
  const systemMetricsList = [
    { key: "totalSignup", label: "Total Signup" },
    { key: "totalSignout", label: "Total Signout" },
    { key: "accountDeleted", label: "Account Deleted" },
    { key: "totalActiveUsers", label: "Total Active Users" },
    { key: "avgActiveTime", label: "Avg. Active Time" },
    { key: "firstConsultation", label: "First Consultation" },
    { key: "repeatConsultation", label: "Repeat Consultation" },
    { key: "totalPaidChatConsultation", label: "Total Paid Chat Consultation" },
    { key: "totalPaidCallConsultation", label: "Total Paid Call Consultation" },
    { key: "firstCallPaidConsultation", label: "1st Call Paid Consultation" },
    { key: "secondCallPaidConsultation", label: "2nd Call Paid Consultation" },
    { key: "thirdCallPaidConsultation", label: "3rd Call Paid Consultation" },
    { key: "fourthCallPaidConsultation", label: "4th Call Paid Consultation" },
    { key: "fifthCallPaidConsultation", label: "5th Call Paid Consultation" },
    { key: "sixthCallPaidConsultation", label: "6th Call Paid Consultation" },
    { key: "seventhCallPaidConsultation", label: "7th Call Paid Consultation" },
    { key: "firstChatPaidConsultation", label: "1st Chat Paid Consultation" },
    { key: "secondChatPaidConsultation", label: "2nd Chat Paid Consultation" },
    { key: "thirdChatPaidConsultation", label: "3rd Chat Paid Consultation" },
    { key: "fourthChatPaidConsultation", label: "4th Chat Paid Consultation" },
    { key: "fifthChatPaidConsultation", label: "5th Chat Paid Consultation" },
    { key: "sixthChatPaidConsultation", label: "6th Chat Paid Consultation" },
    { key: "seventhChatPaidConsultation", label: "7th Chat Paid Consultation" },
    { key: "freeCanceledChatConsultation", label: "Free Canceled Chat Consultation" },
    { key: "freeCancelledCallConsultation", label: "Free Cancelled Call Consultation" },
    { key: "paidMissedCallConsultation", label: "Paid Missed Call Consultation" },
    { key: "paidMissedChatConsultation", label: "Paid Missed Chat Consultation" },
    { key: "noOfFirstRecharge", label: "No. of First Recharge" },
    { key: "totalNoOfRecharge", label: "Total No. of Recharge" },
    { key: "firstRechargeAmount", label: "First Recharge Amount" },
    { key: "totalRechargeAmount", label: "Total Recharge Amount" },
    { key: "spendOnCall", label: "Spend on Call" },
    { key: "spendOnChat", label: "Spend on Chat" },
    { key: "totalCoinsInWallet", label: "Total Coins in Wallet" },
    { key: "timeSpentOnChat", label: "time spent on chat" },
    { key: "timeSpentOnCall", label: "time spent on Call" },
  ];

  useEffect(() => {
    fetchSystemMetrics();
  }, [startDate, endDate]);

  const fetchSystemMetrics = async () => {
    try {
      const { data } = await fetchSystemGeneratedMetricsApi({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      });
      if (data.code === 200) {
        setSystemMetrics(data.result || {});
      }
    } catch (error) {
      console.error("Error fetching system metrics:", error);
    }
  };

  const handleAddMetric = () => {
    setFormData({
      name: "",
      formula: "",
      description: "",
    });
    setEditingIndex(null);
    setFormulaError("");
    setPreviewValue(null);
    setOpenDialog(true);
  };

  const handleEditMetric = (index) => {
    const metric = customMetrics[index];
    setFormData({
      name: metric.name,
      formula: metric.formula,
      description: metric.description || "",
    });
    setEditingIndex(index);
    setFormulaError("");
    setPreviewValue(null);
    setOpenDialog(true);
  };

  const handleDeleteMetric = (index) => {
    const metricToDelete = customMetrics[index];
    const newMetrics = customMetrics.filter((_, i) => i !== index);
    setCustomMetrics(newMetrics);
    saveToLocalStorage(newMetrics);
    
    // Also remove from custom metrics config
    try {
      const existingConfig = JSON.parse(localStorage.getItem("customMetricsConfig") || "[]");
      const updatedConfig = existingConfig.filter((cm) => cm.id !== metricToDelete.id);
      localStorage.setItem("customMetricsConfig", JSON.stringify(updatedConfig));
    } catch (error) {
      console.error("Error updating custom metrics config:", error);
    }
    
    if (onMetricsUpdate) {
      onMetricsUpdate(newMetrics);
    }
    toastMessage("Custom metric deleted", "success");
  };

  const validateFormula = (formula) => {
    if (!formula.trim()) {
      setFormulaError("Formula cannot be empty");
      return false;
    }

    // Replace metric keys with their values for validation
    let testFormula = formula;
    systemMetricsList.forEach((metric) => {
      const regex = new RegExp(`\\b${metric.key}\\b`, "g");
      testFormula = testFormula.replace(regex, "1"); // Replace with 1 for validation
    });

    // Check for valid mathematical expression
    try {
      // Remove spaces and validate
      testFormula = testFormula.replace(/\s/g, "");
      
      // Check for invalid characters
      if (!/^[0-9+\-*/().%\s]+$/.test(testFormula.replace(/[a-zA-Z_]/g, ""))) {
        setFormulaError("Formula contains invalid characters");
        return false;
      }

      // Try to evaluate (with safety check)
      const result = Function(`"use strict"; return (${testFormula})`)();
      if (isNaN(result) || !isFinite(result)) {
        setFormulaError("Formula evaluates to invalid result");
        return false;
      }
    } catch (error) {
      setFormulaError("Invalid formula syntax");
      return false;
    }

    setFormulaError("");
    return true;
  };

  const calculateFormula = (formula) => {
    try {
      let calculatedFormula = formula;
      
      // Replace all metric keys with their actual values
      systemMetricsList.forEach((metric) => {
        const value = systemMetrics[metric.key] || 0;
        const regex = new RegExp(`\\b${metric.key}\\b`, "g");
        calculatedFormula = calculatedFormula.replace(regex, value);
      });

      // Remove spaces
      calculatedFormula = calculatedFormula.replace(/\s/g, "");

      // Evaluate the formula
      const result = Function(`"use strict"; return (${calculatedFormula})`)();
      
      return isNaN(result) || !isFinite(result) ? 0 : result;
    } catch (error) {
      console.error("Error calculating formula:", error);
      return 0;
    }
  };

  const handleFormulaChange = (value) => {
    setFormData({ ...formData, formula: value });
    
    if (value.trim()) {
      if (validateFormula(value)) {
        const preview = calculateFormula(value);
        setPreviewValue(preview);
      } else {
        setPreviewValue(null);
      }
    } else {
      setPreviewValue(null);
      setFormulaError("");
    }
  };

  const handleInsertMetric = (metricKey) => {
    const currentFormula = formData.formula || "";
    const newFormula = currentFormula + (currentFormula ? " " : "") + metricKey;
    setFormData({ ...formData, formula: newFormula });
    handleFormulaChange(newFormula);
  };

  const handleSaveMetric = () => {
    if (!formData.name.trim()) {
      toastMessage("Please enter a metric name", "error");
      return;
    }

    if (!validateFormula(formData.formula)) {
      toastMessage("Please fix the formula errors", "error");
      return;
    }

    const newMetric = {
      id: editingIndex !== null ? customMetrics[editingIndex].id : Date.now(),
      name: formData.name,
      formula: formData.formula,
      description: formData.description,
      value: calculateFormula(formData.formula),
      createdAt: editingIndex !== null 
        ? customMetrics[editingIndex].createdAt 
        : new Date().toISOString(),
    };

    let newMetrics;
    if (editingIndex !== null) {
      newMetrics = [...customMetrics];
      newMetrics[editingIndex] = newMetric;
    } else {
      newMetrics = [...customMetrics, newMetric];
    }

    setCustomMetrics(newMetrics);
    saveToLocalStorage(newMetrics);
    setOpenDialog(false);
    
    if (onMetricsUpdate) {
      onMetricsUpdate(newMetrics);
    }
    
    toastMessage(
      editingIndex !== null ? "Metric updated" : "Metric created",
      "success"
    );
  };

  const saveToLocalStorage = (metrics) => {
    try {
      localStorage.setItem("customMetrics", JSON.stringify(metrics));
      // Update custom metrics config to include new metrics with visible: true
      const existingConfig = JSON.parse(localStorage.getItem("customMetricsConfig") || "[]");
      const updatedConfig = metrics.map((metric) => {
        const existing = existingConfig.find((cm) => cm.id === metric.id);
        return existing || { ...metric, visible: true, isCustom: true };
      });
      localStorage.setItem("customMetricsConfig", JSON.stringify(updatedConfig));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem("customMetrics");
      if (saved) {
        const metrics = JSON.parse(saved);
        setCustomMetrics(metrics);
        if (onMetricsUpdate) {
          onMetricsUpdate(metrics);
        }
      }
    } catch (error) {
      console.error("Error loading from localStorage:", error);
    }
  };

  useEffect(() => {
    loadFromLocalStorage();
  }, []);

  // Recalculate all metrics when system metrics change
  useEffect(() => {
    if (Object.keys(systemMetrics).length > 0 && customMetrics.length > 0) {
      const updatedMetrics = customMetrics.map((metric) => ({
        ...metric,
        value: calculateFormula(metric.formula),
      }));
      setCustomMetrics(updatedMetrics);
      if (onMetricsUpdate) {
        onMetricsUpdate(updatedMetrics);
      }
    }
  }, [systemMetrics]);

  return (
    <Box>
      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#ff8989" }}>
              Custom Metrics Builder
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddMetric}
              sx={{
                backgroundColor: "#ff8989",
                "&:hover": { backgroundColor: "#ff7070" },
              }}
            >
              Create Custom Metric
            </Button>
          </Box>

          {customMetrics.length === 0 ? (
            <Alert severity="info">
              No custom metrics created yet. Click "Create Custom Metric" to get started.
            </Alert>
          ) : (() => {
            // Filter metrics based on visibility config
            const customMetricsConfig = JSON.parse(localStorage.getItem("customMetricsConfig") || "[]");
            const visibleMetrics = customMetrics.length > 0 && customMetricsConfig.length > 0
              ? customMetrics.filter((metric) => {
                  const config = customMetricsConfig.find((cm) => cm.id === metric.id);
                  return config ? config.visible !== false : true; // Default to visible if no config
                })
              : customMetrics; // Show all if no config exists

            if (visibleMetrics.length === 0 && customMetrics.length > 0) {
              return (
                <Alert severity="warning">
                  All custom metrics are hidden. Go to "Manage Metrics" tab to show them.
                </Alert>
              );
            }

            return (
              <Grid container spacing={2}>
                {visibleMetrics.map((metric, index) => {
                  const originalIndex = customMetrics.findIndex((m) => m.id === metric.id);
                  return (
                <Grid item xs={12} sm={6} md={4} key={metric.id}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      "&:hover": {
                        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: "#ff8989" }}
                            >
                              {metric.name}
                            </Typography>
                            {metric.description && (
                              <Tooltip title={metric.description} arrow placement="top">
                                <IconButton
                                  size="small"
                                  sx={{
                                    padding: "2px",
                                    color: "#999",
                                    "&:hover": {
                                      color: "#ff8989",
                                    },
                                  }}
                                >
                                  <InfoIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {metric.formula && (
                              <Tooltip 
                                title={
                                  <Box sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                                    Formula: {metric.formula}
                                  </Box>
                                } 
                                arrow 
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  sx={{
                                    padding: "2px",
                                    color: "#999",
                                    "&:hover": {
                                      color: "#ff8989",
                                    },
                                  }}
                                >
                                  <InfoIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => handleEditMetric(originalIndex)}
                            sx={{ color: "#1976d2" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteMetric(originalIndex)}
                            sx={{ color: "#f44336" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: "#ff8989",
                          mt: 1,
                        }}
                      >
                        {typeof metric.value === "number"
                          ? metric.value.toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })
                          : "0"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                  );
                })}
              </Grid>
            );
          })()}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingIndex !== null ? "Edit" : "Create"} Custom Metric
            </Typography>
            <IconButton onClick={() => setOpenDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Metric Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              sx={{ mb: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Available System Metrics:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  maxHeight: "150px",
                  overflowY: "auto",
                  p: 1,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                {systemMetricsList.map((metric) => (
                  <Chip
                    key={metric.key}
                    label={metric.label}
                    onClick={() => handleInsertMetric(metric.key)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#ff8989",
                        color: "white",
                      },
                    }}
                    size="small"
                  />
                ))}
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Formula"
              value={formData.formula}
              onChange={(e) => handleFormulaChange(e.target.value)}
              placeholder="e.g., (totalPaidCallConsultation / totalFreeCallConsultation) * 100"
              multiline
              rows={3}
              sx={{ mb: 1 }}
              required
              error={!!formulaError}
              helperText={
                formulaError || "Use system metrics with +, -, *, /, % operators"
              }
            />

            {previewValue !== null && !formulaError && (
              <Alert severity="success" sx={{ mt: 1 }}>
                Preview: {previewValue.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </Alert>
            )}

            <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Formula Examples:
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                • Conversion Rate: (totalPaidCallConsultation / firstConsultation) * 100
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                • Avg Revenue: spendOnCall / totalPaidCallConsultation
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                • Usage Ratio: timeSpentOnChat / timeSpentOnCall
              </Typography>
              <Typography variant="body2">
                • Net Users: totalActiveUsers - accountDeleted
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveMetric}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!!formulaError || !formData.name.trim()}
            sx={{
              backgroundColor: "#ff8989",
              "&:hover": { backgroundColor: "#ff7070" },
            }}
          >
            {editingIndex !== null ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomMetricsBuilder;

